import { useSuspenseQuery } from '@apollo/client';
import type { FC } from 'react';
import { Suspense } from 'react';
import { FormattedMessage } from 'react-intl';

import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import type {
  ImageUploadCopy,
  ImageUploadOnPublishData,
} from '@xing-com/crate-entity-pages-common';
import {
  ImageUpload,
  WarningMessageBox as WarningBox,
} from '@xing-com/crate-entity-pages-common';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import { Headline } from '@xing-com/typography';

import { EntityPageOriginalCoverDocument } from '../../../../graphql/queries/original-cover.gql-types';
import { CropSkeleton } from '../../../crop-skeleton/crop-skeleton';

import * as Styled from './update-cover.styles';

type UpdateCoverProps = {
  isCover?: boolean;
  descriptionCopyKey?: string;
  aspectRatio?: number;
  imageUploadCopy?: ImageUploadCopy;
  onPublish?: (data: ImageUploadOnPublishData) => void;
  onCropReady?: () => void;
  onDragCrop?: () => void;
  onDiscard: () => void;
  onCancel: () => void;
};

const ErrorFallback: FC<{ onReloadClick?: () => void }> = ({
  onReloadClick = () => window.location.reload(),
}) => {
  return (
    <>
      <Headline size={'xxlarge'}>
        <Styled.UploadHeadline>
          <FormattedMessage id="EP_CHOOSE_SECTION" />
        </Styled.UploadHeadline>
      </Headline>
      <WarningBox
        headerText="EP_ERROR_HEADER"
        bodyText="EP_ERROR_BODY"
        buttonText="EP_ERROR_RELOAD_CTA"
        onClick={() => onReloadClick}
      />
    </>
  );
};

export const UpdateCoverInner: FC<UpdateCoverProps> = ({
  descriptionCopyKey,
  aspectRatio,
  imageUploadCopy,
  onCropReady = () => undefined,
  onPublish = () => undefined,
  onDragCrop = () => undefined,
  onDiscard = () => undefined,
  onCancel = () => undefined,
}) => {
  const { pageContext } = usePageContext();

  const { data, error, refetch } = useSuspenseQuery(
    EntityPageOriginalCoverDocument,
    {
      fetchPolicy: 'no-cache',
      variables: {
        id: pageContext.pageSlug,
        dimensions: [{ width: 1024, height: 1024, reference: 'cover' }],
      },
    }
  );

  const entityPage =
    data?.entityPageEX?.__typename === 'EntityPage'
      ? data?.entityPageEX
      : undefined;

  const originalImage = entityPage?.originalCoverImage?.[0]?.url;

  if (!originalImage || error) {
    return <ErrorFallback onReloadClick={() => refetch()} />;
  }

  return (
    <ImageUpload
      descriptionCopyKey={descriptionCopyKey}
      onCropReady={onCropReady}
      onDragCrop={onDragCrop}
      onDiscard={onDiscard}
      defaultImageCrop={originalImage}
      onPublish={onPublish}
      aspectRatio={aspectRatio}
      onCancel={onCancel}
      imageUploadCopy={imageUploadCopy}
    />
  );
};

const UpdateCoverFallback: FC<{ isCover: boolean }> = ({ isCover }) => {
  return (
    <>
      <Headline size="xxlarge">
        <Styled.UploadHeadline>
          <FormattedMessage id="EP_CHOOSE_SECTION" />
        </Styled.UploadHeadline>
      </Headline>
      <CropSkeleton isCover={isCover} />
    </>
  );
};

export const UpdateCover: FC<UpdateCoverProps> = ({
  isCover = false,
  ...props
}) => {
  return (
    <ErrorBoundary onError={() => <ErrorFallback />}>
      <Suspense fallback={<UpdateCoverFallback isCover={isCover} />}>
        <UpdateCoverInner {...props} />
      </Suspense>
    </ErrorBoundary>
  );
};
